import React, { useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../../CustomDataGrid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import { useModal } from '../../../../../store/ModalProvider'
import type { TachographServiceTypeModalData, TachographServiceTypeRow } from '../../../../../types/modal'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const tachographTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 16px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 16px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.04)',
	},
}

const tagBaseSx = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '2px 8px',
	borderRadius: '999px',
	border: '1px solid',
	fontSize: '10px',
	fontWeight: 600,
	lineHeight: 1.2,
}

const tagDigitalSx = {
	...tagBaseSx,
	borderColor: '#FF8B2A',
	color: '#FF8B2A',
	backgroundColor: 'rgba(255, 139, 42, 0.12)',
}

const tagAnalogicoSx = {
	...tagBaseSx,
	borderColor: '#00A5FF',
	color: '#00A5FF',
	backgroundColor: 'rgba(0, 165, 255, 0.12)',
}

const tagTextSx = {
	fontSize: '12px',
	color: '#8695AF',
}

const defaultRows: TachographServiceTypeRow[] = [
	{
		id: '1',
		motorista: 'Beatriz Silva',
		numMec: '298',
		admissao: '21/04/2008',
		zona: 'Portimão',
		idZona: '1',
		seg: 'Folga',
		ter: 'Complementar',
		qua: 'Digital',
		qui: 'Analógico',
		sex: 'Analógico',
		sab: 'Digital',
	},
	{
		id: '2',
		motorista: 'Carlos Cardoso',
		numMec: '110',
		admissao: '21/04/2008',
		zona: 'Portimão',
		idZona: '1',
		seg: 'Digital',
		ter: 'Digital',
		qua: 'Analógico',
		qui: 'Folga',
		sex: 'Complementar',
		sab: 'Digital',
	},
	{
		id: '3',
		motorista: 'Rita Madeira',
		numMec: '234',
		admissao: '21/04/2008',
		zona: 'Portimão',
		idZona: '1',
		seg: 'Folga',
		ter: 'Complementar',
		qua: 's/ Viatura',
		qui: 'Digital',
		sex: 'Digital',
		sab: 'Complementar',
	},
	{
		id: '4',
		motorista: 'Vasco Silva',
		numMec: '908',
		admissao: '21/04/2008',
		zona: 'Portimão',
		idZona: '1',
		seg: 'Analógico',
		ter: 's/ Viatura',
		qua: 'Analógico',
		qui: 'Analógico',
		sex: 'Analógico',
		sab: 'Complementar',
	},
	{
		id: '5',
		motorista: 'Vasco Antunes',
		numMec: '780',
		admissao: '21/04/2008',
		zona: 'Portimão',
		idZona: '1',
		seg: 'Digital',
		ter: 'Digital',
		qua: 'Digital',
		qui: 'Complementar',
		sex: 'Complementar',
		sab: 'Complementar',
	},
]

const CustomModalTachographServiceTypes: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as TachographServiceTypeModalData | undefined
	const [handleExport, handlePrint] = modalHandlers ?? []

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [driver, setDriver] = useState<string>(data?.motorista ?? 'Anabela, Isabel, Rui (+2)')
	const [hideNomenclature, setHideNomenclature] = useState<string>(data?.hideNomenclature ?? 'Nenhuma')
	const [density, setDensity] = useState<DensityMode>('compact')

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations(isOpen)

	const rows = data?.rows?.length ? data.rows : defaultRows
	const driverOptions = useMemo(() => {
		const uniqueDrivers = new Set(rows.map(row => row.motorista).filter(Boolean))
		if (driver && !uniqueDrivers.has(driver)) {
			uniqueDrivers.add(driver)
		}
		return Array.from(uniqueDrivers)
	}, [rows, driver])

	const renderTag = (value: string) => {
		if (value === 'Digital') {
			return <Box sx={tagDigitalSx}>Digital</Box>
		}
		if (value === 'Analógico') {
			return <Box sx={tagAnalogicoSx}>Analógico</Box>
		}
		return <Typography sx={tagTextSx}>{value}</Typography>
	}

	const columns = useMemo<GridColDef<TachographServiceTypeRow>[]>(
		() => [
			{
				field: 'motorista',
				headerName: 'Motorista',
				width: 190,
				renderCell: params => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Avatar
							src={params.row.avatarUrl}
							sx={{
								borderRadius: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								bgcolor: 'lightgrey 50% / cover no-repeat',
								width: 32,
								height: 32,
								fontSize: '20px',
								fontWeight: 400,
								lineHeight: 1,
							}}
						>
							{params.value?.[0] ?? 'M'}
						</Avatar>
						<Typography sx={{ fontSize: '12px' }}>{params.value}</Typography>
					</Box>
				),
			},
			{ field: 'numMec', headerName: 'Num Mec', width: 80 },
			{ field: 'admissao', headerName: 'Admissão', width: 100 },
			{ field: 'zona', headerName: 'Zona', width: 90 },
			{ field: 'idZona', headerName: 'ID Zona', width: 70 },
			{
				field: 'seg',
				headerName: 'Seg - 01 Nov',
				width: 110,
				renderCell: params => renderTag(String(params.value ?? '')),
			},
			{
				field: 'ter',
				headerName: 'Ter - 02 Nov',
				width: 110,
				renderCell: params => renderTag(String(params.value ?? '')),
			},
			{
				field: 'qua',
				headerName: 'Qua - 03 Nov',
				width: 110,
				renderCell: params => renderTag(String(params.value ?? '')),
			},
			{
				field: 'qui',
				headerName: 'Qui - 04 Nov',
				width: 110,
				renderCell: params => renderTag(String(params.value ?? '')),
			},
			{
				field: 'sex',
				headerName: 'Sex - 05 Nov',
				width: 110,
				renderCell: params => renderTag(String(params.value ?? '')),
			},
			{
				field: 'sab',
				headerName: 'Sáb - 06 Nov',
				width: 110,
				renderCell: params => renderTag(String(params.value ?? '')),
			},
		],
		[],
	)

	const handleClearFilters = () => {
		setStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setDriver('Anabela, Isabel, Rui (+2)')
		setHideNomenclature('Nenhuma')
	}

	const handleExportRows = () => {
		if (typeof handleExport === 'function') {
			handleExport(rows)
			return
		}
		console.log('Exportar serviços por tipo de tacógrafo', rows)
	}

	const handlePrintRows = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir serviços por tipo de tacógrafo', rows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Serviços por Tipo de Tacógrafo
					</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: 0.5 }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ padding: '0 24px 24px', display: 'grid', gap: '24px' }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data Inicial'
								value={startDate}
								onChange={newValue => setStartDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
							{String.fromCharCode(0x2014)}
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data Final'
								value={endDate}
								onChange={newValue => setEndDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<TextField
							select
							label='Motorista'
							value={driver}
							onChange={event => setDriver(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '200px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							{driverOptions.map(option => (
								<MenuItem
									key={option}
									value={option}
								>
									{option}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label='Ocultar Nomenclaturas'
							value={hideNomenclature}
							onChange={event => setHideNomenclature(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '200px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							{['Nenhuma', 'Folga', 'Complementar', 's/ Viatura'].map(option => (
								<MenuItem
									key={option}
									value={option}
								>
									{option}
								</MenuItem>
							))}
						</TextField>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleClearFilters}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '12px',
								textTransform: 'none',
								marginLeft: 'auto',
								padding: '4px 0',
								'&:hover': { backgroundColor: 'transparent' },
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: '16px',
							minHeight: '32px',
						}}
					>
						<Typography
							sx={{
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.235,
								color: '#092056',
							}}
						>
							{rows.length} Motoristas
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_, value) => value && setDensity(value)}
							size='small'
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espaçada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={columns}
						density={density}
						animateRows={animateRows}
						columnHeaderHeight={40}
						disableRowSelectionOnClick
						pagination={true}
						pageSize={10}
						sx={[tableSx, tachographTableSx]}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '8px 24px 16px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						onClick={handleExportRows}
						sx={popupSecondaryButtonSx}
					>
						Exportar
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handlePrintRows}
						sx={popupActionButtonSx}
					>
						Imprimir
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalTachographServiceTypes
